<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
    <!-- 页面头部 -->
    <div class="flex items-center gap-4 mb-6">
      <button @click="handleBack"
        class="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">
        <ArrowLeftIcon class="w-6 h-6" />
      </button>
      <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">编辑公告</h1>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-20">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <!-- 表单 -->
    <MessageForm v-else-if="initialData" mode="edit" :initial-data="initialData" @submit="handleSubmit"
      @cancel="handleBack" />

    <!-- 错误状态 -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
      <p class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">公告不存在</p>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">该公告可能已被删除</p>
      <button @click="handleBack"
        class="primary-btn px-6">
        返回列表
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ArrowLeftIcon } from '@heroicons/vue/24/outline'
import MessageForm from '@/components/message/MessageForm.vue'
import { getMessageAPI, updateMessageAPI } from '@/api/message'
import toaster from '@/utils/toaster'
import type { CreateMessageRequest, UpdateMessageRequest } from '@/types/message'

/**
 * 路由实例
 */
const router = useRouter()
const route = useRoute()

/**
 * 初始数据
 */
const initialData = ref<Partial<CreateMessageRequest> | null>(null)
const loading = ref(true)

/**
 * 加载公告数据
 */
const loadMessage = async () => {
  const messageId = parseInt(route.params.id as string)
  if (isNaN(messageId)) {
    toaster.error('无效的公告ID')
    router.push('/message')
    return
  }

  try {
    loading.value = true
    const response = await getMessageAPI(messageId)

    if (response.status === 200 || response.status === 'success') {
      // API返回的数据结构: { message: {...}, stats: {...} }
      // 直接使用,前后端字段名已统一
      initialData.value = {
        title: response.data.message.title,
        content: response.data.message.content,
        priority: response.data.message.priority,
        isPopup: response.data.message.isPopup,
        validFrom: response.data.message.validFrom,
        validUntil: response.data.message.validUntil,
      }
    } else {
      toaster.error(response.message || '获取公告详情失败')
      initialData.value = null
    }
  } catch (error: any) {
    console.error('获取公告详情失败:', error)
    toaster.error(error.message || '获取公告详情失败')
    initialData.value = null
  } finally {
    loading.value = false
  }
}

/**
 * 返回详情页
 */
const handleBack = () => {
  const messageId = route.params.id
  router.push(`/message/${messageId}`)
}

/**
 * 处理表单提交
 */
const handleSubmit = async (data: CreateMessageRequest | UpdateMessageRequest) => {
  const messageId = parseInt(route.params.id as string)
  if (isNaN(messageId)) {
    toaster.error('无效的公告ID')
    return
  }

  try {
    // 编辑模式下,data已经是UpdateMessageRequest类型
    const response = await updateMessageAPI(messageId, data as UpdateMessageRequest)

    if (response.status === 200 || response.status === 'success') {
      toaster.success('公告更新成功')
      router.push(`/message/${messageId}`)
    } else {
      toaster.error(response.message || '公告更新失败')
    }
  } catch (error: any) {
    console.error('更新公告失败:', error)
    toaster.error(error.message || '公告更新失败,请稍后重试')
  }
}

/**
 * 组件挂载时加载数据
 */
onMounted(() => {
  loadMessage()
})
</script>
